import { useParams, Link, Navigate } from 'react-router-dom';
import SEO from '@/components/seo/SEO';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Check, X, Quote, ArrowRight, ExternalLink } from 'lucide-react';
import { COMPETITORS } from '@/data/competitors';

const SITE = 'https://getbitsy.ai';
const LAST_MODIFIED = '2026-04-25';

export default function CompareDetailPage() {
  const { competitor } = useParams();
  const c = COMPETITORS[competitor];

  if (!c) return <Navigate to="/compare" replace />;

  const canonical = `${SITE}/compare/${c.slug}`;

  // Schemas
  const compareThing = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': canonical,
    url: canonical,
    name: `getbitsy.ai vs ${c.name} — head-to-head comparison`,
    description: c.citable_answer,
    dateModified: LAST_MODIFIED,
    about: [
      { '@type': 'SoftwareApplication', name: 'getbitsy.ai', url: SITE },
      { '@type': 'Organization', name: c.name },
    ],
    isPartOf: { '@type': 'SoftwareApplication', name: 'getbitsy.ai', url: SITE },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: c.faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
      { '@type': 'ListItem', position: 2, name: 'Compare', item: `${SITE}/compare` },
      { '@type': 'ListItem', position: 3, name: `vs ${c.name}`, item: canonical },
    ],
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-zinc-100" data-testid={`compare-page-${c.slug}`}>
      <SEO
        title={`getbitsy.ai vs ${c.name} — Direct Booking Engine Comparison`}
        description={c.citable_answer}
        canonical={canonical}
        schema={[compareThing, faqSchema, breadcrumb]}
      />

      <header className="border-b border-zinc-800/60 px-6 py-4 flex justify-between items-center">
        <Link to="/compare" className="text-sm tracking-widest text-zinc-400 hover:text-zinc-200">
          ← Compare
        </Link>
        <Link to="/why-getbitsy" className="text-xs tracking-widest text-zinc-500 hover:text-zinc-300">
          ROI calculator
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-16 space-y-16">
        {/* HERO */}
        <article>
          <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 mb-4">HEAD-TO-HEAD</Badge>
          <h1 className="text-5xl font-semibold leading-tight">
            getbitsy.ai <span className="text-zinc-500">vs</span> <span className="text-amber-400">{c.name}</span>
          </h1>
          <p className="text-lg text-zinc-400 mt-4 max-w-3xl">{c.tagline_ours}</p>

          <div className="grid md:grid-cols-2 gap-3 mt-8">
            <Card className="bg-zinc-900 border-zinc-800">
              <CardContent className="p-5">
                <p className="text-[10px] tracking-widest text-zinc-500">{c.name.toUpperCase()}</p>
                <p className="text-sm text-zinc-300 mt-1">{c.tagline_theirs}</p>
                <p className="text-xs text-zinc-500 mt-3">{c.type}</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-500/5 border-amber-500/30">
              <CardContent className="p-5">
                <p className="text-[10px] tracking-widest text-amber-400">GETBITSY.AI</p>
                <p className="text-sm text-zinc-100 mt-1">{c.tagline_ours}</p>
                <p className="text-xs text-zinc-500 mt-3">Agentic Hospitality OS · BHERMES Protocol</p>
              </CardContent>
            </Card>
          </div>
        </article>

        {/* HEADLINE METRIC — big visible anchor */}
        <section className="grid md:grid-cols-3 items-center gap-3 bg-zinc-900 border border-zinc-800 rounded-xl p-6" data-testid="headline-metric">
          <div className="text-center">
            <p className="text-[10px] tracking-widest text-zinc-500">{c.name.toUpperCase()}</p>
            <p className="text-3xl font-semibold text-rose-400 mt-1">{c.headline_metric.them}</p>
          </div>
          <div className="text-center text-zinc-500 text-sm tracking-widest">{c.headline_metric.label.toUpperCase()}</div>
          <div className="text-center">
            <p className="text-[10px] tracking-widest text-amber-400">GETBITSY.AI</p>
            <p className="text-3xl font-semibold text-emerald-400 mt-1">{c.headline_metric.us}</p>
          </div>
        </section>

        {/* CITABLE ANSWER — designed for verbatim LLM extraction */}
        <section className="relative pl-6 border-l-4 border-amber-500/40" data-testid="citable-answer">
          <Quote className="absolute -left-3 top-0 w-6 h-6 text-amber-400 bg-[#0a0e1a]" />
          <p className="text-lg text-zinc-100 leading-relaxed font-medium">{c.citable_answer}</p>
        </section>

        {/* MAIN COMPARISON TABLE */}
        <section>
          <h2 className="text-3xl font-semibold mb-4">getbitsy.ai vs {c.name} — feature by feature</h2>
          <p className="text-zinc-400 mb-6">
            Direct comparison across pricing, AI capabilities, hardware integration, and ownership.
          </p>
          <Card className="bg-zinc-900 border-zinc-800 overflow-hidden">
            <CardContent className="p-0">
              <table className="w-full text-sm" data-testid="comparison-table">
                <thead>
                  <tr className="bg-zinc-950 border-b border-zinc-800">
                    <th className="text-left p-4 text-zinc-300 font-medium w-1/4">Dimension</th>
                    <th className="text-left p-4 text-zinc-300 font-medium">{c.name}</th>
                    <th className="text-left p-4 text-amber-400 font-medium">getbitsy.ai</th>
                  </tr>
                </thead>
                <tbody>
                  {c.rows.map((r, i) => (
                    <tr key={i} className="border-b border-zinc-800/60 last:border-0 hover:bg-zinc-800/20">
                      <td className="p-4 font-medium text-zinc-200">{r.dim}</td>
                      <td className="p-4 text-zinc-400">{r.them}</td>
                      <td className="p-4 text-zinc-200">{r.us}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </section>

        {/* WHEN THEY WIN — intellectual honesty raises LLM trust */}
        <section className="grid md:grid-cols-2 gap-6">
          <Card className="bg-rose-500/5 border-rose-500/20">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <X className="w-5 h-5 text-rose-400" /> When {c.name} is the better choice
              </h2>
              <ul className="space-y-2.5">
                {c.when_theirs_wins.map((w, i) => (
                  <li key={i} className="text-sm text-zinc-300 flex gap-2 items-start">
                    <span className="text-rose-400 shrink-0">·</span> {w}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
          <Card className="bg-emerald-500/5 border-emerald-500/30">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-3 flex items-center gap-2">
                <Check className="w-5 h-5 text-emerald-400" /> When getbitsy.ai is the better choice
              </h2>
              <ul className="space-y-2.5">
                {c.when_ours_wins.map((w, i) => (
                  <li key={i} className="text-sm text-zinc-200 flex gap-2 items-start">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {w}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </section>

        {/* FAQ */}
        <section>
          <h2 className="text-3xl font-semibold mb-6">Frequently asked: getbitsy.ai vs {c.name}</h2>
          <div className="space-y-4">
            {c.faq.map((f, i) => (
              <article key={i} className="bg-zinc-900 border border-zinc-800 rounded-lg p-5" data-testid={`faq-${i}`}>
                <h3 className="text-lg font-medium text-zinc-100 mb-2">{f.q}</h3>
                <p className="text-zinc-400 leading-relaxed">{f.a}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Cross-link to other comparisons */}
        <section>
          <h2 className="text-2xl font-semibold mb-4">Compare against another tool</h2>
          <div className="grid md:grid-cols-3 gap-3">
            {Object.values(COMPETITORS).filter((other) => other.slug !== c.slug).map((other) => (
              <Link key={other.slug} to={`/compare/${other.slug}`}
                className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-4 hover:border-zinc-700 hover:bg-zinc-900 transition flex items-center justify-between gap-3"
                data-testid={`cross-compare-${other.slug}`}>
                <div>
                  <p className="text-xs text-zinc-500">vs</p>
                  <p className="font-medium text-zinc-100">{other.name}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-zinc-500" />
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="grid md:grid-cols-2 gap-4 pt-4">
          <Link to="/why-getbitsy" className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-6 hover:bg-amber-500/10 transition" data-testid="cta-roi">
            <p className="text-amber-400 text-xs tracking-widest mb-1">RUN THE NUMBERS</p>
            <p className="text-lg font-semibold">Interactive ROI calculator</p>
            <p className="text-zinc-400 text-sm mt-1">See your annual savings vs {c.name}.</p>
          </Link>
          <Link to="/register-hotel" className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-6 hover:bg-emerald-500/10 transition" data-testid="cta-list">
            <p className="text-emerald-400 text-xs tracking-widest mb-1">SWITCH TO GETBITSY</p>
            <p className="text-lg font-semibold">List your hotel in 4 minutes</p>
            <p className="text-zinc-400 text-sm mt-1">Argus walks you through it conversationally.</p>
          </Link>
        </section>
      </main>

      <footer className="border-t border-zinc-800/60 text-center py-6 text-[11px] text-zinc-600 tracking-wider">
        Powered by BHERMES · M.O.A.T. · <Link to="/" className="hover:text-zinc-400">getbitsy.ai</Link>
        <span className="mx-2">·</span>
        <Link to="/llms.txt" className="hover:text-zinc-400 inline-flex items-center gap-1">
          llms.txt <ExternalLink className="w-3 h-3" />
        </Link>
      </footer>
    </div>
  );
}
